import { DesignerKitLink } from "@/components/DesignerKit";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Seo } from "@/components/Seo";
import { Loader } from "@/components/brand/Mascot";
import { MascotDoodle } from "@/components/brand/MascotDoodle";
import { Brush } from "@/components/brand/Lettering";
import { Barcode, Crown, Smiley, Sparkle } from "@/components/brand/Doodles";
import { Reveal } from "@/components/Reveal";
import { WinnersWall } from "@/components/sotm/WinnersWall";
import { ClosedBanner, ShareMenu, VotingCountdown, WinnerReveal } from "@/components/sotm/SotmBits";
import { deviceId, errText, getSotmGallery, getSotmSubmission, monthLabel, shareImg, voteSotm } from "@/lib/api";
import { breadcrumb } from "@/lib/site";
import { pointsToast, useAuth } from "@/context/AuthContext";

const STEPS = [[Sparkle, "Submit", "Teens 13–19 send a tee design"], [Smiley, "Community votes", "One vote per design, per device"], [Crown, "We pick", "Our crew picks the final winner"], [Barcode, "Your tee drops", "Sold in the shop, with your story"]];
const VOTED_KEY = "sus-voted";
const loadVoted = () => { try { return JSON.parse(localStorage.getItem(VOTED_KEY)) || []; } catch { return []; } };

const Card = ({ s, voted, onVote, open, focused }) => (
  <article className={`group ${focused ? "outline outline-[3px] outline-offset-8 outline-pink" : ""}`} id={`design-${s.id}`} data-testid={`sotm-card-${s.id}`}>
    <div className="tile relative aspect-square overflow-hidden">
      <img src={s.design_url} alt={`${s.design_title}, tee design by ${s.first_name} from ${s.city}`} loading="lazy" className="h-full w-full object-cover mix-blend-multiply transition-transform duration-700 group-hover:scale-[1.03]" />
      {s.status === "winner" && <span className="sticker absolute left-3 top-3 bg-lime">Winner</span>}
      {focused && <span className="sticker absolute right-3 top-3 bg-pink" data-testid="sotm-focused-badge">Vote for me</span>}
    </div>
    <div className="mt-4 flex items-start justify-between gap-3">
      <div>
        <h3 className="text-[15px]">{s.design_title}</h3>
        <p className="text-[13px] text-ink/55">{s.first_name} · {s.city}</p>
      </div>
      <button onClick={() => (!open ? toast("Votes are in. Voting is closed.") : voted ? toast("You already voted for this one 👀") : onVote(s))} aria-pressed={voted} aria-disabled={!open} title={!open ? "Voting is closed" : voted ? "You already voted for this one 👀" : "Vote for this design"} className={`label flex shrink-0 items-center gap-2 border-[1.5px] border-ink px-3 py-2 font-bold transition-colors ${voted || !open ? "cursor-default bg-ink text-cloud" : "hover:bg-lime"} ${!open ? "opacity-60" : ""}`} data-testid={`sotm-vote-${s.id}`}>
        {!open ? "Closed" : voted ? "Voted" : "Vote"} <span data-testid={`sotm-votes-${s.id}`}>{s.votes}</span>
      </button>
    </div>
    {s.status === "approved" && <ShareMenu s={s} testid={`sotm-share-${s.id}`} />}
  </article>
);

const WinnerFeature = ({ w }) => (
  <section className="grid items-center gap-10 border-y border-ink/10 py-16 lg:grid-cols-12" aria-labelledby="winner-title" data-testid="sotm-current-winner">
    <div className="tile aspect-square overflow-hidden rounded-[24px] lg:col-span-5"><img src={w.design_url} alt={`${w.design_title} by ${w.full_name}`} className="h-full w-full object-cover mix-blend-multiply" /></div>
    <div className="lg:col-span-6 lg:col-start-7">
      <p className="label text-ink/50">Winner · {monthLabel(w.month)}</p>
      <h2 id="winner-title" className="display mt-4 text-[clamp(3rem,6vw,5.5rem)]">{w.design_title}</h2>
      <p className="mt-4 text-lg">by {w.full_name}, {w.age} · {w.city}</p>
      <p className="mt-5 max-w-md text-[15px] leading-relaxed text-ink/70">{w.story}</p>
      <Link to={`/sus-of-the-month/${w.slug}`} className="btn btn-ink mt-8" data-testid="sotm-winner-link">Read the story + shop the tee <ArrowRight size={16} /></Link>
    </div>
  </section>
);

const Status = ({ data }) => {
  if (data.month_winner) return <WinnerReveal w={data.month_winner} />;
  if (!data.voting_open) return <ClosedBanner rows={data.leaderboard} />;
  return <div className="rounded-[24px] border-[1.5px] border-ink bg-sky p-6 sm:p-8"><VotingCountdown closesAt={data.voting_closes_at} /></div>;
};

export default function SotmGallery() {
  const { id } = useParams();
  const { addPoints } = useAuth();
  const [data, setData] = useState(null);
  const [focus, setFocus] = useState(null);
  const [voted, setVoted] = useState(loadVoted);
  useEffect(() => { getSotmGallery().then(setData).catch(() => setData({ submissions: [], archive: [], voting_open: false, leaderboard: [] })); }, []);
  useEffect(() => { if (id) getSotmSubmission(id).then(setFocus).catch(() => toast.error("That design isn't in the lineup.")); }, [id]);
  useEffect(() => {
    if (data && focus) setTimeout(() => document.getElementById(`design-${focus.id}`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 300);
  }, [data, focus]);

  const markVoted = (sid) => setVoted((v) => { const n = [...new Set([...v, sid])]; localStorage.setItem(VOTED_KEY, JSON.stringify(n)); return n; });
  const onVote = async (s) => {
    try {
      const r = await voteSotm(s.id, deviceId());
      setData((d) => ({ ...d, submissions: d.submissions.map((x) => (x.id === s.id ? { ...x, votes: r.votes } : x)) }));
      markVoted(s.id);
      toast.success(r.message);
      if (r.points) { addPoints(r.points); pointsToast(r.points, "vote"); }
    } catch (e) {
      if (e.response?.status === 409) markVoted(s.id);
      if (e.response?.status === 403) setData((d) => ({ ...d, voting_open: false }));
      toast.error(errText(e));
    }
  };

  if (!data) return <Loader label="Loading the lineup" />;
  const prevWinner = data.current_winner && data.current_winner.month !== data.month ? data.current_winner : null;
  const seo = focus
    ? { title: `Vote for ${focus.design_title} by ${focus.first_name}`, description: `${focus.first_name} from ${focus.city} is in the Sus of the Month lineup at the suspects. One tap to vote.`, path: `/sus-of-the-month/vote/${focus.id}`, image: shareImg(focus.id, "square") }
    : { title: "Sus of the Month: teen tee design contest", description: "Every month, GTA teens aged 13 to 19 submit tee designs, the community votes, and the suspects prints the winner with the creator's story. Submit your design or vote now.", path: "/sus-of-the-month" };
  return (
    <div className="mx-auto max-w-[1440px] px-5 pb-28 sm:px-10" data-testid="sotm-gallery-page">
      <Seo {...seo} jsonLd={[breadcrumb([{ name: "Home", path: "/" }, { name: "Sus of the Month", path: "/sus-of-the-month" }])]} />
      <header className="grid items-end gap-8 pb-14 pt-16 lg:grid-cols-12 lg:pt-24">
        <div className="relative lg:col-span-8">
          <p className="label text-ink/55">Monthly · Ages 13–19 · Greater Toronto</p>
          <h1 className="display mt-5 text-[clamp(4rem,10vw,8.75rem)]">Sus of the <span className="hl hl-pink hl-thick">month</span></h1>
        </div>
        <div className="lg:col-span-4 lg:justify-self-end">
          <Brush lines={["Your design.", "Our tees."]} className="text-[clamp(2.25rem,3.5vw,3.25rem)]" />
          <div className="mt-8 flex flex-wrap items-center gap-6"><Link to="/sus-of-the-month/submit" className="btn btn-lime" data-testid="sotm-submit-cta">Submit your design <ArrowRight size={16} /></Link><DesignerKitLink testid="sotm-kit-link" /></div>
        </div>
      </header>
      <div className="mb-14"><Status data={data} /></div>
      <ol className="grid grid-cols-2 gap-px border-y border-ink/10 bg-ink/10 lg:grid-cols-4" aria-label="How it works">
        {STEPS.map(([Icon, t, d], i) => (
          <li key={t} className="bg-cloud px-5 py-8 sm:px-8">
            <div className="flex items-center justify-between"><Icon size={36} /><span className="label text-ink/40">0{i + 1}</span></div>
            <p className="display mt-6 text-2xl">{t}</p>
            <p className="mt-2 text-[13px] text-ink/60">{d}</p>
          </li>
        ))}
      </ol>
      {prevWinner && !data.month_winner && <div className="mt-20"><WinnerFeature w={prevWinner} /></div>}
      <section className="pt-20" aria-labelledby="lineup-title">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <h2 id="lineup-title" className="display text-[clamp(2.75rem,5vw,4.5rem)]">The lineup · {monthLabel(data.month)}</h2>
          <p className="label text-ink/50" data-testid="sotm-voting-state">{data.voting_open ? "Voting open · one vote per design" : "Voting closed"}</p>
        </div>
        {data.submissions.length === 0 ? (
          <div className="flex flex-col items-center gap-5 py-20 text-center" data-testid="sotm-lineup-empty"><MascotDoodle variant="headphones" size={130} /><p className="text-lg">No designs yet this month. Be the first.</p></div>
        ) : (
          <div className="mt-10 grid grid-cols-2 gap-x-4 gap-y-14 sm:gap-x-6 lg:grid-cols-3" data-testid="sotm-lineup-grid">
            {data.submissions.map((s, i) => <Reveal key={s.id} delay={(i % 3) * 80}><Card s={s} voted={voted.includes(s.id)} onVote={onVote} open={data.voting_open} focused={focus?.id === s.id} /></Reveal>)}
          </div>
        )}
      </section>
      <div className="mt-24 border-t border-ink/10 pt-16" data-testid="sotm-archive">
        <WinnersWall />
        <Link to="/sus-of-the-month/wall" className="link-u" data-testid="sotm-wall-link">Open the full wall <ArrowRight size={14} /></Link>
      </div>
    </div>
  );
}
